import React from 'react';
import {
  Box,
  IconButton,
  LinearProgress,
  Link,
  Popover,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import type { ScreenAudienceBucketData } from '../types';
import { ANALYTICS_CANVAS_TEST_IDS } from '../testIds';
import { formatPct } from '../statTierCopy';
import type { AudienceProvenanceSettings } from '../audienceProvenance';

/**
 * Provenance buckets UI (brief 2026-07-22, approved mock
 * audience-provenance.html) — shared by AnalyticsNodeDrawer's AudienceSection
 * and DropoffAudienceDrawer. A hidden bucket renders as a dashed DISCLOSURE
 * row with its people count (never silently dropped), or — when the persisted
 * switch is on — as an expanded card: title + meaning caption + value rows
 * with the bucket's OWN denominator + per-bucket «no value recorded»
 * residual. Empty buckets render nothing (D-6). The inline show/hide links
 * flip the SAME persisted switches as the ⋯ settings popover.
 */

/** D-5 parity with both drawers: per-row honesty caption below this n. */
const DIRECTION_ONLY_MAX_N = 30;

type BucketKey = 'after' | 'other';

interface BucketListProps {
  buckets?: { after?: ScreenAudienceBucketData; other?: ScreenAudienceBucketData };
  settings: AudienceProvenanceSettings;
  onToggle: (bucket: BucketKey, shown: boolean) => void;
  /** Meaning caption inside the expanded «after» card (surface-specific). */
  afterCaption: string;
  /** Meaning caption inside the expanded «other» card (surface-specific). */
  otherCaption: string;
  /** Bar color matches the hosting drawer (audience = primary, drop-off = error). */
  barColor?: 'primary' | 'error';
}

export function AudienceProvenanceBucketList({
  buckets,
  settings,
  onToggle,
  afterCaption,
  otherCaption,
  barColor = 'primary',
}: BucketListProps): React.ReactElement | null {
  if (!buckets || (!buckets.after && !buckets.other)) return null;
  return (
    <>
      {buckets.after && (
        <ProvenanceBucket
          bucketKey="after"
          bucket={buckets.after}
          shown={settings.showAfterScreen}
          onToggle={onToggle}
          disclosureLabel="Answered after this screen (same visit)"
          title={`Answered after this screen · same visit · ${buckets.after.people.toLocaleString('en-US')}`}
          caption={afterCaption}
          barColor={barColor}
        />
      )}
      {buckets.other && (
        <ProvenanceBucket
          bucketKey="other"
          bucket={buckets.other}
          shown={settings.showOtherVisits}
          onToggle={onToggle}
          disclosureLabel="Values from other visits"
          title={`From other visits · ${buckets.other.people.toLocaleString('en-US')} returnees`}
          caption={otherCaption}
          barColor={barColor}
        />
      )}
    </>
  );
}

function ProvenanceBucket({
  bucketKey,
  bucket,
  shown,
  onToggle,
  disclosureLabel,
  title,
  caption,
  barColor,
}: {
  bucketKey: BucketKey;
  bucket: ScreenAudienceBucketData;
  shown: boolean;
  onToggle: (bucket: BucketKey, shown: boolean) => void;
  disclosureLabel: string;
  title: string;
  caption: string;
  barColor: 'primary' | 'error';
}): React.ReactElement {
  if (!shown) {
    return (
      <Stack
        direction="row"
        spacing={1}
        justifyContent="space-between"
        alignItems="center"
        onClick={() => onToggle(bucketKey, true)}
        data-testid={
          bucketKey === 'after'
            ? ANALYTICS_CANVAS_TEST_IDS.audienceBucketDisclosureAfter
            : ANALYTICS_CANVAS_TEST_IDS.audienceBucketDisclosureOther
        }
        sx={{
          border: '1px dashed',
          borderColor: 'divider',
          borderRadius: 1,
          px: 1.25,
          py: 0.75,
          cursor: 'pointer',
        }}
      >
        <Typography variant="caption" color="text.secondary">
          {disclosureLabel}
          {' · '}
          <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>
            {bucket.people.toLocaleString('en-US')}
          </Box>
          {' people'}
        </Typography>
        <Link component="button" type="button" variant="caption" underline="none" sx={{ fontWeight: 600 }}>
          show
        </Link>
      </Stack>
    );
  }

  const people = bucket.people;
  return (
    <Stack
      spacing={0.75}
      data-testid={
        bucketKey === 'after'
          ? ANALYTICS_CANVAS_TEST_IDS.audienceBucketAfter
          : ANALYTICS_CANVAS_TEST_IDS.audienceBucketOther
      }
      sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, px: 1.25, py: 1 }}
    >
      <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="baseline">
        <Typography variant="overline" sx={{ lineHeight: 1.4 }}>{title}</Typography>
        <Link
          component="button"
          type="button"
          variant="caption"
          underline="none"
          color="text.secondary"
          onClick={() => onToggle(bucketKey, false)}
        >
          hide
        </Link>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
        {caption}
      </Typography>
      {bucket.variables.map(variable => (
        <Stack key={variable.variableKey} spacing={0.5}>
          <Typography variant="overline" color="text.secondary">{variable.variableKey}</Typography>
          {variable.values.map(entry => (
            <BucketShareRow
              key={entry.value}
              label={entry.value}
              users={entry.users}
              share={entry.share}
              barColor={barColor}
              directionOnly={entry.users < DIRECTION_ONLY_MAX_N}
            />
          ))}
          {variable.noValueUsers > 0 && (
            <BucketShareRow
              label="no value recorded"
              users={variable.noValueUsers}
              share={people > 0 ? variable.noValueUsers / people : 0}
              barColor={barColor}
              residual
            />
          )}
        </Stack>
      ))}
    </Stack>
  );
}

/** Rows inside a bucket card — shares use the BUCKET's own denominator. */
function BucketShareRow({
  label,
  users,
  share,
  barColor,
  directionOnly = false,
  residual = false,
}: {
  label: string;
  users: number;
  share: number;
  barColor: 'primary' | 'error';
  directionOnly?: boolean;
  residual?: boolean;
}): React.ReactElement {
  return (
    <Box>
      <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="baseline">
        <Typography
          variant="body2"
          sx={{
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            ...(residual && { color: 'text.secondary', fontStyle: 'italic' }),
          }}
        >
          {label}
          {directionOnly && (
            <Typography component="span" variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', ml: 0.5 }}>
              ~ direction only
            </Typography>
          )}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
          {`${users.toLocaleString('en-US')} · ${formatPct(share)}`}
        </Typography>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={Math.max(0, Math.min(100, share * 100))}
        color={residual ? 'inherit' : barColor}
        sx={{ height: 4, borderRadius: 1, ...(residual && { opacity: 0.45 }) }}
      />
    </Box>
  );
}

/**
 * The ⋯ settings popover (section header): two persisted switches — one
 * state with the inline disclosure links, per the approved mock.
 */
export function AudienceProvenanceSettingsButton({
  settings,
  onToggle,
}: {
  settings: AudienceProvenanceSettings;
  onToggle: (bucket: BucketKey, shown: boolean) => void;
}): React.ReactElement {
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  return (
    <>
      <IconButton
        size="small"
        aria-label="audience profile settings"
        data-testid={ANALYTICS_CANVAS_TEST_IDS.audienceProvenanceSettingsTrigger}
        onClick={event => setAnchorEl(event.currentTarget)}
        sx={{ p: 0.25 }}
      >
        <MoreHorizIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
      </IconButton>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Stack spacing={0.75} sx={{ p: 1.5, width: 300 }} data-testid={ANALYTICS_CANVAS_TEST_IDS.audienceProvenanceSettings}>
          <Typography variant="subtitle2">Audience profile</Typography>
          <SettingsRow
            bucketKey="after"
            label="Show answers given after this screen"
            detail="same visit; went further & came back"
            checked={settings.showAfterScreen}
            onToggle={onToggle}
          />
          <SettingsRow
            bucketKey="other"
            label="Show values from other visits"
            detail="earlier passes through the funnel"
            checked={settings.showOtherVisits}
            onToggle={onToggle}
          />
          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            Both off by default. Applies to «Who reached this screen» and «Left after …» alike;
            the inline show/hide links flip the same switches.
          </Typography>
        </Stack>
      </Popover>
    </>
  );
}

function SettingsRow({
  bucketKey,
  label,
  detail,
  checked,
  onToggle,
}: {
  bucketKey: BucketKey;
  label: string;
  detail: string;
  checked: boolean;
  onToggle: (bucket: BucketKey, shown: boolean) => void;
}): React.ReactElement {
  return (
    <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2">{label}</Typography>
        <Typography variant="caption" color="text.secondary">{detail}</Typography>
      </Box>
      {/* testid on the ROOT: the transparent input covers it, so a center
          click (Playwright) lands on the checkbox; RTL reaches it by role */}
      <Switch
        size="small"
        checked={checked}
        onChange={(_event, value) => onToggle(bucketKey, value)}
        data-testid={ANALYTICS_CANVAS_TEST_IDS.audienceProvenanceSwitch(bucketKey)}
      />
    </Stack>
  );
}
